import React, { useState, useEffect } from 'react';
import '../css/UploadPage.css';
import { FolderOpen } from 'lucide-react';
import { db } from '../firebase'; // Import your existing Firebase setup
import { collection, doc, setDoc, getDoc, getDocs, query, where, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage'; // Import Firebase Storage
import * as pdfjsLib from 'pdfjs-dist';

// Set worker Src to matching CDN version
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

const CanvasPDFViewer = ({ url }) => {
  const containerRef = React.useRef(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    const renderPDF = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument(url);
        const pdf = await loadingTask.promise;
        
        if (!active) return;
        
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }

        // Render each page of the PDF to its own canvas
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          if (!active) break;
          const page = await pdf.getPage(pageNum);
          
          // Render at 1.5x scale for good resolution
          const viewport = page.getViewport({ scale: 1.5 });
          
          const canvas = document.createElement('canvas');
          canvas.style.display = 'block';
          canvas.style.marginBottom = '15px';
          canvas.style.width = '100%';
          canvas.style.maxWidth = '650px';
          canvas.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
          canvas.style.borderRadius = '4px';
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          
          const context = canvas.getContext('2d');
          const renderContext = {
            canvasContext: context,
            viewport: viewport,
          };
          
          if (containerRef.current) {
            containerRef.current.appendChild(canvas);
          }
          
          await page.render(renderContext).promise;
        }
        
        if (active) setLoading(false);
      } catch (err) {
        console.error('PDF rendering error:', err);
        if (active) {
          setError(err.message || 'Failed to render PDF document.');
          setLoading(false);
        }
      }
    };

    renderPDF();

    return () => {
      active = false;
    };
  }, [url]);

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {loading && <div style={{ padding: '20px', color: '#666', fontSize: '14px' }}>Loading document preview pages...</div>}
      {error && <div style={{ padding: '20px', color: '#dc2626', fontSize: '14px' }}>Error rendering PDF: {error}</div>}
      <div ref={containerRef} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }} />
    </div>
  );
};

const UploadPage = () => {
  const [selectedNewspaper, setSelectedNewspaper] = useState('Nirnayak');
  const [selectedEdition, setSelectedEdition] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedPage, setSelectedPage] = useState('');
  const [weekStartDate, setWeekStartDate] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [message, setMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [dateType, setDateType] = useState('daily'); // 'daily' or 'weekly'
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isNotifying, setIsNotifying] = useState(false);

  // New state for preview modal
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [multipleFiles, setMultipleFiles] = useState([]); // State for batch uploads

  // State for page upload popup modal
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [popupSelectedFiles, setPopupSelectedFiles] = useState({}); // { pageNum: File }
  const [popupUploadProgress, setPopupUploadProgress] = useState({}); // { pageNum: percent }
  const [popupUploadStatus, setPopupUploadStatus] = useState({}); // { pageNum: 'uploading' | 'success' | 'error' }
  const [popupUploadError, setPopupUploadError] = useState({}); // { pageNum: message }

  // API URL for email service
  const API_URL = process.env.REACT_APP_API_URL || 'https://api-olbm2m4ljq-uc.a.run.app';

  const newspapers = ['Nirnayak', 'Shikhar Sanket'];
  
  const getEditionsForNewspaper = (newspaper) => {
    if (newspaper === 'Shikhar Sanket') {
      return ['Ujjain', 'Agar'];
    }
    return ['Ujjain', 'Indore']; // Default/Nirnayak
  };
  
  const currentEditions = getEditionsForNewspaper(selectedNewspaper);
  const pageNumbers = Array.from({ length: 8 }, (_, i) => i + 1);
  const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Initialize Firebase Storage
  const storage = getStorage();

  // Generate date options: Today, Yesterday, Tomorrow
  const getTodayDate = () => {
    const today = new Date();
    return formatDate(today);
  };

  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return formatDate(yesterday);
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDate(tomorrow);
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  };

  const formatDisplayDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Get date for a specific weekday based on week start date
  const getDateForWeekday = (weekStart, dayIndex) => {
    if (!weekStart) return '';
    const date = new Date(weekStart);
    date.setDate(date.getDate() + dayIndex);
    return formatDate(date);
  };

  useEffect(() => {
    // Initialize with today's date
    const today = getTodayDate();
    setSelectedDate(today); // Set today as default selected date

    // Calculate the Monday of current week for weekly view
    const currentDate = new Date();
    const dayOfWeek = currentDate.getDay();
    const diff = currentDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust when day is Sunday
    const monday = new Date(currentDate.setDate(diff));
    setWeekStartDate(formatDate(monday));

    // Fetch existing uploads from Firebase
    fetchUploads();
  }, []);

  // Fetch uploads based on current selection
  const fetchUploads = async () => {
    try {
      const newUploadedFiles = {};
      let datesToFetch = [];

      if (dateType === 'daily') {
        datesToFetch = [getTodayDate(), getYesterdayDate(), getTomorrowDate()];
      } else {
        if (!weekStartDate) return;
        for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
          datesToFetch.push(getDateForWeekday(weekStartDate, dayIndex));
        }
      }

      // Pre-initialize structure
      for (const date of datesToFetch) {
        newUploadedFiles[date] = {};
        for (const newspaper of newspapers) {
          newUploadedFiles[date][newspaper] = {};
          const editionsForThisNP = getEditionsForNewspaper(newspaper);
          for (const edition of editionsForThisNP) {
            const totalPages = newspaper === 'Shikhar Sanket' ? 1 : 8;
            newUploadedFiles[date][newspaper][edition] = Array(totalPages).fill(null);
          }
        }
      }

      // Fetch all docs for these dates in one query
      const q = query(
        collection(db, 'pages'),
        where('date', 'in', datesToFetch)
      );
      const querySnapshot = await getDocs(q);

      querySnapshot.forEach(docSnap => {
        const data = docSnap.data();
        const date = data.date;
        const newspaper = data.newspaper || 'Nirnayak';
        const edition = data.edition;
        const pageNum = data.pageNumber;

        if (date && newspaper && edition && pageNum && newUploadedFiles[date]?.[newspaper]?.[edition]) {
          const pageIndex = pageNum - 1;
          newUploadedFiles[date][newspaper][edition][pageIndex] = {
            name: data.name,
            url: data.url,
            status: data.status,
            uploaded: data.timestamp
          };
        }
      });

      setUploadedFiles(newUploadedFiles);
    } catch (error) {
      console.error("Error fetching uploads:", error);
      setMessage("Failed to load existing uploads. Please try again.");
    }
  };

  // Send email notification about the page submission
  const sendPageSubmissionNotification = async (pageData) => {
    try {
      setIsNotifying(true);
      setMessage("Sending notification to admin...");

      // Prepare message for admin
      const subject = `New Page Submission for Review - ${pageData.edition} Edition, Page ${pageData.pageNumber}`;
      const messageText = `
A new newspaper page has been submitted for review:

Edition: ${pageData.edition}
Page Number: ${pageData.pageNumber}
Date: ${formatDisplayDate(pageData.date)}
Submission Time: ${new Date().toLocaleString()}

Please review the page at your earliest convenience.
Approve here: nirnayaknews.in/admin
      `;

      // Send notification to admin
      const response = await fetch(`${API_URL}/api/notify/review-submission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: 'adityaniltiwari@gmail.com',
          subject: subject,
          message: messageText,
          attachment: {
            url: pageData.url,
            filename: pageData.name
          }
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to send notification: ${response.statusText}`);
      }

      const result = await response.json();
      console.log("Notification result:", result);

      setMessage("Notification sent to admin for review");
    } catch (error) {
      console.error("Error sending notification:", error);
      setMessage(`Page uploaded successfully, but failed to notify admin: ${error.message}`);
    } finally {
      setIsNotifying(false);
    }
  };

  // Handle date type change (daily/weekly)
  const handleDateTypeChange = (e) => {
    setDateType(e.target.value);
    if (e.target.value === 'daily') {
      setSelectedDate(getTodayDate()); // Default to today
    } else {
      setSelectedDate(''); // Reset for weekly view
    }
    setSelectedEdition('');
    setSelectedPage('');
    // Refetch uploads
    fetchUploads();
  };

  // Handle week change for weekly view
  const handleWeekChange = (e) => {
    setWeekStartDate(e.target.value);
    setSelectedDate('');
    setSelectedEdition('');
    setSelectedPage('');
    // Fetch uploads for the new week
    fetchUploads();
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setSelectedEdition('');
    setSelectedPage('');
  };

  const handleNewspaperChange = (e) => {
    const val = e.target.value;
    setSelectedNewspaper(val);
    setSelectedEdition('');
    if (val === 'Shikhar Sanket') {
      setSelectedPage('1');
    } else {
      setSelectedPage('');
    }
  };

  const handleEditionChange = (e) => {
    setSelectedEdition(e.target.value);
    if (selectedNewspaper === 'Shikhar Sanket') {
      setSelectedPage('1');
    } else {
      setSelectedPage('');
    }
  };

  const handlePageChange = (e) => {
    setSelectedPage(e.target.value);
  };

  const handleFileChange = (e) => {
    if (!selectedDate || !selectedEdition) {
      setMessage('Please select a date and edition first.');
      return;
    }

    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check file format
    const validFormats = ['application/pdf', 'image/jpeg', 'image/png'];
    const invalidFiles = files.filter(f => !validFormats.includes(f.type));
    if (invalidFiles.length > 0) {
      setMessage('Invalid file format. Please upload PDF, JPG, or PNG files.');
      return;
    }

    // Auto-detect page numbers from filenames (looking for numbers 1 to 8)
    const newFiles = files.map(file => {
      const match = file.name.match(/[1-8]/);
      const autoPage = match ? match[0] : '';
      return {
        file,
        pageNumber: autoPage || (selectedPage || ''),
        status: 'selected', // 'selected', 'uploading', 'success', 'error'
        progress: 0,
        error: ''
      };
    });

    if (newFiles.length === 1 && selectedPage) {
      // Single file with page number selected -> Use original preview/single upload flow
      const file = newFiles[0].file;
      const pageIndex = parseInt(selectedPage, 10) - 1;
      const newUploadedFiles = { ...uploadedFiles };
      const totalPages = selectedNewspaper === 'Shikhar Sanket' ? 1 : 8;

      if (!newUploadedFiles[selectedDate]) newUploadedFiles[selectedDate] = {};
      if (!newUploadedFiles[selectedDate][selectedNewspaper]) newUploadedFiles[selectedDate][selectedNewspaper] = {};
      if (!newUploadedFiles[selectedDate][selectedNewspaper][selectedEdition]) {
        newUploadedFiles[selectedDate][selectedNewspaper][selectedEdition] = Array(totalPages).fill(null);
      }

      newUploadedFiles[selectedDate][selectedNewspaper][selectedEdition][pageIndex] = {
        file,
        name: `${selectedNewspaper}_${selectedDate}_${selectedEdition}_Page${selectedPage}.${file.name.split('.').pop()}`,
        status: 'selected',
        uploaded: new Date().toLocaleString()
      };

      setUploadedFiles(newUploadedFiles);
      setMultipleFiles([]);
      setMessage(`${file.name} selected successfully. Review the preview below.`);

      const previewInfo = {
        newspaper: selectedNewspaper,
        date: selectedDate,
        edition: selectedEdition,
        pageNum: parseInt(selectedPage, 10),
        fileInfo: newUploadedFiles[selectedDate][selectedNewspaper][selectedEdition][pageIndex],
        previewUrl: URL.createObjectURL(file)
      };

      setPreviewData(previewInfo);
      setShowPreview(true);
    } else {
      // Multiple files or no page number selected -> Go to batch upload list mode
      setMultipleFiles(newFiles);
      setMessage(`${newFiles.length} files selected. Assign page numbers and click upload.`);
    }
  };

  const uploadMultipleFiles = async () => {
    const unassigned = multipleFiles.filter(f => !f.pageNumber);
    if (unassigned.length > 0) {
      alert("Please assign a page number to all selected files.");
      return;
    }

    const pageNumbersList = multipleFiles.map(f => f.pageNumber);
    const duplicates = pageNumbersList.filter((item, index) => pageNumbersList.indexOf(item) !== index);
    if (duplicates.length > 0) {
      alert(`Duplicate page numbers assigned: Page ${[...new Set(duplicates)].join(', ')}. Please assign unique page numbers.`);
      return;
    }

    setIsUploading(true);
    setMessage('Starting batch upload...');

    const newUploadedFiles = { ...uploadedFiles };
    const totalPages = selectedNewspaper === 'Shikhar Sanket' ? 1 : 8;

    for (let i = 0; i < multipleFiles.length; i++) {
      const fileObj = multipleFiles[i];
      const pageNum = parseInt(fileObj.pageNumber, 10);
      const pageIndex = pageNum - 1;

      setMultipleFiles(prev => prev.map((item, index) => index === i ? { ...item, status: 'uploading' } : item));

      const fileExtension = fileObj.file.name.split('.').pop();
      const fileName = `${selectedNewspaper}_${selectedDate}_${selectedEdition}_Page${pageNum}_${Date.now()}.${fileExtension}`;
      
      try {
        const storageRef = ref(storage, `newspaper_pages/${fileName}`);
        const uploadTask = uploadBytesResumable(storageRef, fileObj.file);

        const url = await new Promise((resolve, reject) => {
          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setMultipleFiles(prev => prev.map((item, index) => index === i ? { ...item, progress } : item));
            },
            (err) => reject(err),
            async () => {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(downloadURL);
            }
          );
        });

        const pageData = {
          newspaper: selectedNewspaper,
          name: `${selectedNewspaper}_${selectedDate}_${selectedEdition}_Page${pageNum}.${fileExtension}`,
          url: url,
          status: 'review',
          timestamp: new Date().toISOString(),
          date: selectedDate,
          edition: selectedEdition,
          pageNumber: pageNum,
          storagePath: `newspaper_pages/${fileName}`
        };

        const docId = selectedNewspaper === 'Nirnayak' 
          ? `${selectedDate}_${selectedEdition}_Page${pageNum}` 
          : `${selectedNewspaper}_${selectedDate}_${selectedEdition}_Page${pageNum}`;
          
        const docRef = doc(db, 'pages', docId);
        await setDoc(docRef, pageData);

        if (!newUploadedFiles[selectedDate]) newUploadedFiles[selectedDate] = {};
        if (!newUploadedFiles[selectedDate][selectedNewspaper]) newUploadedFiles[selectedDate][selectedNewspaper] = {};
        if (!newUploadedFiles[selectedDate][selectedNewspaper][selectedEdition]) {
          newUploadedFiles[selectedDate][selectedNewspaper][selectedEdition] = Array(totalPages).fill(null);
        }

        newUploadedFiles[selectedDate][selectedNewspaper][selectedEdition][pageIndex] = {
          name: pageData.name,
          url: url,
          status: 'review',
          uploaded: new Date().toLocaleString()
        };

        // Notify in background
        sendPageSubmissionNotification(pageData);

        setMultipleFiles(prev => prev.map((item, index) => index === i ? { ...item, status: 'success', progress: 100 } : item));

      } catch (error) {
        console.error(`Error uploading page ${pageNum}:`, error);
        setMultipleFiles(prev => prev.map((item, index) => index === i ? { ...item, status: 'error', error: error.message } : item));
      }
    }

    setUploadedFiles(newUploadedFiles);
    setIsUploading(false);
    setMessage('Batch upload completed!');
    alert('All selected pages have been uploaded and submitted for review!');
    
    setTimeout(() => {
      setMultipleFiles([]);
    }, 3000);
  };

  const uploadToFirebaseStorage = async (file, fileName) => {
    setIsUploading(true);
    setUploadProgress(0);

    // Create a reference to the file in Firebase Storage
    const storageRef = ref(storage, `newspaper_pages/${fileName}`);

    // Upload the file
    const uploadTask = uploadBytesResumable(storageRef, file);

    // Set up progress monitoring
    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot) => {
          // Track upload progress
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
          setMessage(`Uploading: ${progress.toFixed(0)}%`);
        },
        (error) => {
          // Handle errors
          console.error('Error uploading file:', error);
          setIsUploading(false);
          setUploadProgress(0);
          setMessage(`Failed to upload file: ${error.message}. Please try again.`);
          reject(error);
        },
        async () => {
          // Upload completed successfully
          try {
            // Get the download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            setIsUploading(false);
            setUploadProgress(0);
            setMessage('File uploaded successfully!');
            resolve(downloadURL);
          } catch (error) {
            console.error('Error getting download URL:', error);
            setIsUploading(false);
            setUploadProgress(0);
            setMessage(`Failed to get download URL: ${error.message}`);
            reject(error);
          }
        }
      );
    });
  };

  // Handle review click - now opens preview modal
  const handleReviewClick = (newspaper, date, edition, pageNum) => {
    const pageIndex = pageNum - 1;

    // Safely access nested object
    const fileInfo = uploadedFiles[date]?.[newspaper]?.[edition]?.[pageIndex];

    if (!fileInfo) {
      setMessage('No file selected for this page.');
      return;
    }

    // Create preview data
    const previewInfo = {
      newspaper,
      date,
      edition,
      pageNum,
      fileInfo,
      previewUrl: fileInfo.url || (fileInfo.file ? URL.createObjectURL(fileInfo.file) : null)
    };

    setPreviewData(previewInfo);
    setShowPreview(true);
  };

  const handlePopupFileChange = (e, pageNum) => {
    const file = e.target.files[0];
    if (!file) return;

    const validFormats = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!validFormats.includes(file.type)) {
      alert('Invalid file format. Please upload PDF, JPG, or PNG files.');
      return;
    }

    setPopupSelectedFiles(prev => ({
      ...prev,
      [pageNum]: file
    }));
    setPopupUploadStatus(prev => {
      const copy = { ...prev };
      delete copy[pageNum];
      return copy;
    });
    setPopupUploadError(prev => {
      const copy = { ...prev };
      delete copy[pageNum];
      return copy;
    });
  };

  const uploadSinglePageFromPopup = async (pageNum) => {
    const file = popupSelectedFiles[pageNum];
    if (!file) return;

    setPopupUploadStatus(prev => ({
      ...prev,
      [pageNum]: 'uploading'
    }));
    setPopupUploadProgress(prev => ({
      ...prev,
      [pageNum]: 0
    }));

    const fileExtension = file.name.split('.').pop();
    const fileName = `${selectedNewspaper}_${selectedDate}_${selectedEdition}_Page${pageNum}_${Date.now()}.${fileExtension}`;

    try {
      const storageRef = ref(storage, `newspaper_pages/${fileName}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      const url = await new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setPopupUploadProgress(prev => ({
              ...prev,
              [pageNum]: progress
            }));
          },
          (err) => reject(err),
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          }
        );
      });

      const pageData = {
        newspaper: selectedNewspaper,
        name: `${selectedNewspaper}_${selectedDate}_${selectedEdition}_Page${pageNum}.${fileExtension}`,
        url: url,
        status: 'review',
        timestamp: new Date().toISOString(),
        date: selectedDate,
        edition: selectedEdition,
        pageNumber: pageNum,
        storagePath: `newspaper_pages/${fileName}`
      };

      const docId = selectedNewspaper === 'Nirnayak' 
        ? `${selectedDate}_${selectedEdition}_Page${pageNum}` 
        : `${selectedNewspaper}_${selectedDate}_${selectedEdition}_Page${pageNum}`;
        
      const docRef = doc(db, 'pages', docId);
      await setDoc(docRef, pageData);

      const pageIndex = pageNum - 1;
      const totalPages = selectedNewspaper === 'Shikhar Sanket' ? 1 : 8;
      const newUploadedFiles = { ...uploadedFiles };
      if (!newUploadedFiles[selectedDate]) newUploadedFiles[selectedDate] = {};
      if (!newUploadedFiles[selectedDate][selectedNewspaper]) newUploadedFiles[selectedDate][selectedNewspaper] = {};
      if (!newUploadedFiles[selectedDate][selectedNewspaper][selectedEdition]) {
        newUploadedFiles[selectedDate][selectedNewspaper][selectedEdition] = Array(totalPages).fill(null);
      }

      newUploadedFiles[selectedDate][selectedNewspaper][selectedEdition][pageIndex] = {
        name: pageData.name,
        url: url,
        status: 'review',
        uploaded: new Date().toLocaleString()
      };
      setUploadedFiles(newUploadedFiles);

      sendPageSubmissionNotification(pageData);

      setPopupUploadStatus(prev => ({
        ...prev,
        [pageNum]: 'success'
      }));

    } catch (error) {
      console.error(`Error uploading page ${pageNum}:`, error);
      setPopupUploadStatus(prev => ({
        ...prev,
        [pageNum]: 'error'
      }));
      setPopupUploadError(prev => ({
        ...prev,
        [pageNum]: error.message
      }));
    }
  };

  // Confirm and proceed with upload
  const confirmUpload = async () => {
    if (!previewData) return;

    const { newspaper, date, edition, pageNum, fileInfo } = previewData;
    const pageIndex = pageNum - 1;

    setShowPreview(false);
    setMessage('Uploading to Firebase Storage...');

    // Create a unique filename
    const fileName = `${newspaper}_${date}_${edition}_Page${pageNum}_${Date.now()}.${fileInfo.file.name.split('.').pop()}`;

    try {
      // Upload to Firebase Storage
      const url = await uploadToFirebaseStorage(fileInfo.file, fileName);
      if (!url) return;

      setMessage('Saving to Firebase Firestore...');

      // Page data to be saved in Firestore and sent in notification
      const pageData = {
        newspaper: newspaper,
        name: fileInfo.name,
        url: url,
        status: 'review',
        timestamp: new Date().toISOString(),
        date: date,
        edition: edition,
        pageNumber: pageNum,
        storagePath: `newspaper_pages/${fileName}`
      };

      // Save to Firebase Firestore
      const docId = newspaper === 'Nirnayak' 
        ? `${date}_${edition}_Page${pageNum}` 
        : `${newspaper}_${date}_${edition}_Page${pageNum}`;
        
      const docRef = doc(db, 'pages', docId);
      await setDoc(docRef, pageData);

      // Update the local state
      const newUploadedFiles = { ...uploadedFiles };
      if (!newUploadedFiles[date]) newUploadedFiles[date] = {};
      if (!newUploadedFiles[date][newspaper]) newUploadedFiles[date][newspaper] = {};
      const totalPages = newspaper === 'Shikhar Sanket' ? 1 : 8;
      if (!newUploadedFiles[date][newspaper][edition]) newUploadedFiles[date][newspaper][edition] = Array(totalPages).fill(null);

      newUploadedFiles[date][newspaper][edition][pageIndex] = {
        ...fileInfo,
        url: url,
        status: 'review',
        uploaded: new Date().toLocaleString()
      };
      setUploadedFiles(newUploadedFiles);

      // Send email notification
      await sendPageSubmissionNotification(pageData);

      // Reset the selected page after successful upload
      setSelectedPage('');

      setMessage(`${fileInfo.name} uploaded successfully and is now in review. Admin has been notified.`);

      // Clean up preview URL
      if (previewData.previewUrl) {
        URL.revokeObjectURL(previewData.previewUrl);
      }
      setPreviewData(null);

      alert("Page has been uploaded and sent for review!");

    } catch (error) {
      console.error('Error in upload process:', error);
      setMessage(`Failed to upload: ${error.message}. Please try again.`);
    }
  };

  // Cancel preview
  const cancelPreview = () => {
    if (previewData && previewData.previewUrl) {
      URL.revokeObjectURL(previewData.previewUrl);
    }
    setPreviewData(null);
    setShowPreview(false);
    setMessage('Upload cancelled. Please select a file again if needed.');
  };

  const pageIsCommon = (pageNum) => {
    return pageNum !== 1 && pageNum !== 8;
  };

  const getStatusIcon = (status) => {
    if (status === 'accepted') {
      return '✅';
    } else if (status === 'rejected') {
      return '❌';
    } else if (status === 'review') {
      return '⏳';
    }
    return '';
  };

  // Check if a page is available for upload
  const isPageAvailableForUpload = (newspaper, date, edition, pageNum) => {
    if (!newspaper || !date || !edition) return false;

    const pageIndex = pageNum - 1;
    // Safely access nested objects
    const fileInfo = uploadedFiles[date]?.[newspaper]?.[edition]?.[pageIndex];

    // Page is available if:
    // 1. No file has been uploaded yet
    // 2. Previous upload was rejected
    return !fileInfo || fileInfo.status === 'rejected';
  };

  const getDateOptions = () => {
    if (dateType === 'daily') {
      return (
        <select value={selectedDate} onChange={handleDateChange}>
          <option value="">-- Select Date --</option>
          <option value={getTodayDate()}>Today ({formatDisplayDate(getTodayDate())})</option>
          <option value={getYesterdayDate()}>Yesterday ({formatDisplayDate(getYesterdayDate())})</option>
          <option value={getTomorrowDate()}>Tomorrow ({formatDisplayDate(getTomorrowDate())})</option>
        </select>
      );
    } else {
      // For weekly view
      return (
        <>
          <div className="form-group">
            <label>Select Week Starting:</label>
            <input
              type="date"
              value={weekStartDate}
              onChange={handleWeekChange}
            />
          </div>
          <div className="form-group">
            <label>Select Day:</label>
            <select value={selectedDate} onChange={handleDateChange}>
              <option value="">-- Select Day --</option>
              {weekDays.map((day, index) => {
                const date = getDateForWeekday(weekStartDate, index);
                return (
                  <option key={index} value={date}>
                    {day} ({formatDisplayDate(date)})
                  </option>
                );
              })}
            </select>
          </div>
        </>
      );
    }
  };

  const renderPageItem = (newspaper, date, edition, pageNum, fileInfo, isCommon) => {
    const isShikharSanket = newspaper === 'Shikhar Sanket';
    const label = isShikharSanket ? 'Full Newspaper' : `Page ${pageNum}`;
    const previewUrl = fileInfo ? (fileInfo.url || (fileInfo.file ? URL.createObjectURL(fileInfo.file) : null)) : null;
    const isPDF = previewUrl && (previewUrl.toLowerCase().split('?')[0].endsWith('.pdf') || (fileInfo.file && fileInfo.file.type === 'application/pdf'));

    return (
      <div
        key={pageNum}
        className={`page-item ${isCommon ? 'common-page' : 'unique-page'} ${fileInfo ? 'uploaded' : 'pending'}`}
        style={{ cursor: 'pointer', padding: '15px', display: 'flex', flexDirection: 'column', gap: '8px' }}
        onClick={() => {
          if (fileInfo) {
            handleReviewClick(newspaper, date, edition, pageNum);
          } else {
            setSelectedNewspaper(newspaper);
            setSelectedDate(date);
            setSelectedEdition(edition);
            setShowUploadPopup(true);
          }
        }}
      >
        <div className="page-number">
          {label} {fileInfo && fileInfo.status && getStatusIcon(fileInfo.status)}
        </div>
        
        {/* Direct thumbnail image/PDF preview */}
        {fileInfo && previewUrl && (
          <div className="page-thumbnail-container" style={{ width: '100%', height: '120px', overflow: 'hidden', borderRadius: '6px', border: '1px solid #cbd5e1', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f1f5f9' }}>
            {isPDF ? (
              !showPreview && (
                <iframe
                  src={previewUrl}
                  title={label}
                  style={{ width: '100%', height: '100%', border: 'none', pointerEvents: 'none' }}
                />
              )
            ) : (
              <img
                src={previewUrl}
                alt={label}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            )}
          </div>
        )}

        <div className="page-status">
          {fileInfo ? (
            <>
              <div style={{ wordBreak: 'break-word', fontSize: '11px', fontWeight: '500' }}>{fileInfo.name}</div>
              <div className="upload-time">{fileInfo.uploaded}</div>
              {fileInfo.status === 'rejected' && (
                <div className="rejected-message">Rejected. Please upload again.</div>
              )}
            </>
          ) : (
            'Not uploaded'
          )}
        </div>
        
        {fileInfo && (
          <button
            className="common-tag"
            onClick={(e) => {
              e.stopPropagation();
              handleReviewClick(newspaper, date, edition, pageNum);
            }}
            disabled={isUploading || isNotifying || fileInfo.status === 'accepted' || fileInfo.status === 'review'}
          >
            {fileInfo.status === 'accepted' ? 'Accepted' :
              fileInfo.status === 'review' ? 'In Review' : 'Preview'}
          </button>
        )}
      </div>
    );
  };

  const renderUploadSummary = () => {
    // If no date is selected, display a message to select a date
    if (!selectedDate) {
      return <div className="no-data">Please select a date to view uploads</div>;
    }

    // Ensure we have data for the selected date
    if (!uploadedFiles[selectedDate]) {
      return <div className="no-data">No uploads found for {formatDisplayDate(selectedDate)}</div>;
    }

    // Show summary only for the selected date
    return (
      <div className="upload-summary">
        <h3>Upload Summary for {formatDisplayDate(selectedDate)}</h3>
        {newspapers.map(newspaper => {
          const editionsForNP = getEditionsForNewspaper(newspaper);
          return (
          <div key={newspaper} className="newspaper-summary" style={{ marginBottom: '30px', padding: '15px', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
            <h3 style={{ marginTop: '0', borderBottom: '2px solid #cbd5e1', paddingBottom: '8px', color: '#1e293b' }}>📰 {newspaper}</h3>
            <div className="date-container" style={{ marginTop: '15px' }}>
              {editionsForNP.map(edition => (
                <div key={`${selectedDate}_${newspaper}_${edition}`} className="edition-summary">
                  <h4>{edition} Edition</h4>
                  <div className="page-grid">
                    {uploadedFiles[selectedDate]?.[newspaper]?.[edition]?.map((fileInfo, index) => {
                      const pageNum = index + 1;
                      const isCommon = pageIsCommon(pageNum);
                      return renderPageItem(newspaper, selectedDate, edition, pageNum, fileInfo, isCommon);
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )})}
      </div>
    );
  };


  return (
    <div className="upload-page-container">
      <h2>Upload Newspaper Pages</h2>

      <div className="upload-section">
        <div className="form-group">
          <label>Select Paper Type:</label>
          <select value={dateType} onChange={handleDateTypeChange}>
            <option value="daily">Daily Papers</option>
            <option value="weekly">Weekly Papers</option>
          </select>
        </div>

        {getDateOptions()}

        <div className="form-group">
          <label>Select Newspaper:</label>
          <select
            value={selectedNewspaper}
            onChange={handleNewspaperChange}
          >
            <option value="">-- Select Newspaper --</option>
            {newspapers.map(np => (
              <option key={np} value={np}>{np}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Select Edition:</label>
          <select
            value={selectedEdition}
            onChange={handleEditionChange}
            disabled={!selectedDate || !selectedNewspaper}
          >
            <option value="">-- Select Edition --</option>
            {currentEditions.map(edition => (
              <option key={edition} value={edition}>{edition} Edition</option>
            ))}
          </select>
        </div>

        {selectedNewspaper !== 'Shikhar Sanket' && (
          <div className="form-group">
            <label>Select Page Number:</label>
            <select
              value={selectedPage}
              onChange={handlePageChange}
              disabled={!selectedEdition || !selectedDate || !selectedNewspaper}
            >
              <option value="">-- Select Page --</option>
              {pageNumbers.map(num => {
                const isAvailable = isPageAvailableForUpload(selectedNewspaper, selectedDate, selectedEdition, num);
                const status = selectedDate && selectedEdition && selectedNewspaper ?
                  (uploadedFiles[selectedDate]?.[selectedNewspaper]?.[selectedEdition]?.[num - 1]?.status || 'available') :
                  'available';
  
                return (
                  <option
                    key={num}
                    value={num}
                    disabled={!isAvailable}
                    style={{ color: isAvailable ? 'black' : '#aaa' }}
                  >
                    Page {num} {num !== 1 && num !== 8 ? '(Common)' : '(Unique)'}
                    {status === 'review' ? ' - In Review' :
                      status === 'accepted' ? ' - Accepted' : ''}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        <div className="form-group" style={{ marginTop: '20px', marginBottom: '25px' }}>
          <label style={{ fontSize: '15px', color: '#1e293b', display: 'block', marginBottom: '8px' }}>
            Popup Upload Option (Recommended):
          </label>
          <button
            type="button"
            className="open-panel-btn"
            onClick={() => setShowUploadPopup(true)}
            disabled={!selectedNewspaper || !selectedEdition || !selectedDate}
          >
            <FolderOpen size={20} className="btn-icon" />
            Open Page Upload Panel (Pages 1 to 8)
          </button>
        </div>

        <div className="form-group">
          <label>Alternative: Upload File(s) (PDF, JPG, PNG):</label>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            multiple
            onChange={handleFileChange}
            disabled={!selectedNewspaper || !selectedEdition || !selectedDate || isUploading || isNotifying}
          />
          <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
            💡 Select one file with a Page Number for single page preview, or select multiple files to upload at once.
          </p>
        </div>

        {multipleFiles.length > 0 && (
          <div className="multiple-files-list" style={{ marginTop: '20px', marginBottom: '20px', padding: '15px', border: '1px solid #cbd5e1', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
            <h4 style={{ margin: '0 0 15px 0', color: '#1e293b' }}>Select Page Numbers for Upload</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {multipleFiles.map((fileObj, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  <div style={{ flex: 1, marginRight: '15px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>{fileObj.file.name}</span>
                    {fileObj.status === 'uploading' && <span style={{ marginLeft: '10px', fontSize: '12px', color: '#0284c7' }}>(Uploading: {fileObj.progress.toFixed(0)}%)</span>}
                    {fileObj.status === 'success' && <span style={{ marginLeft: '10px', fontSize: '12px', color: '#16a34a' }}>(Success)</span>}
                    {fileObj.status === 'error' && <span style={{ marginLeft: '10px', fontSize: '12px', color: '#dc2626' }}>(Error: {fileObj.error})</span>}
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <select
                      value={fileObj.pageNumber}
                      onChange={(e) => {
                        const newPage = e.target.value;
                        setMultipleFiles(prev => prev.map((item, index) => index === idx ? { ...item, pageNumber: newPage } : item));
                      }}
                      disabled={isUploading || fileObj.status === 'success' || fileObj.status === 'uploading'}
                      style={{ padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                    >
                      <option value="">-- Page --</option>
                      {pageNumbers.map(num => (
                        <option key={num} value={num}>Page {num}</option>
                      ))}
                    </select>

                    <button
                      type="button"
                      onClick={() => setMultipleFiles(prev => prev.filter((_, index) => index !== idx))}
                      disabled={isUploading || fileObj.status === 'success' || fileObj.status === 'uploading'}
                      style={{ backgroundColor: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '18px' }}
                      title="Remove file"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                type="button"
                className="confirm-button"
                onClick={uploadMultipleFiles}
                disabled={isUploading || multipleFiles.every(f => f.status === 'success')}
                style={{ padding: '10px 20px', backgroundColor: '#0284c7', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
              >
                {isUploading ? 'Uploading...' : 'Upload All Files'}
              </button>
              
              <button
                type="button"
                onClick={() => setMultipleFiles([])}
                disabled={isUploading}
                style={{ padding: '10px 20px', backgroundColor: '#e2e8f0', color: '#334155', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {message && <div className="message">{message}</div>}

        {isUploading && (
          <div className="progress-bar-container">
            <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
            <span className="progress-text">{uploadProgress.toFixed(0)}%</span>
          </div>
        )}
      </div>

      {renderUploadSummary()}

      {/* Preview Modal */}
      {showPreview && previewData && (() => {
        const { date, edition, pageNum, fileInfo, previewUrl } = previewData;
        const fileType = fileInfo.file ? fileInfo.file.type : '';
        const isPDF = previewUrl && (previewUrl.toLowerCase().split('?')[0].endsWith('.pdf') || fileType === 'application/pdf');
        const isImage = (previewUrl && !isPDF) || (fileType && fileType.startsWith('image/'));

        return (
          <div className="preview-modal-overlay">
            <div className="preview-modal">
              <div className="preview-header">
                <h3>Review Page Preview</h3>
                <button className="close-button" onClick={cancelPreview}>×</button>
              </div>

              <div className="preview-info">
                <p><strong>Newspaper:</strong> {previewData.newspaper}</p>
                <p><strong>Date:</strong> {formatDisplayDate(date)}</p>
                <p><strong>Edition:</strong> {edition}</p>
                <p><strong>Page Number:</strong> {previewData.newspaper === 'Shikhar Sanket' ? 'Full Newspaper' : pageNum}</p>
                <p><strong>File:</strong> {fileInfo.name || (fileInfo.file ? fileInfo.file.name : '')}</p>
              </div>

              <div className="preview-content" style={{ alignItems: isPDF ? 'flex-start' : 'center', minHeight: 'auto' }}>
                {isPDF ? (
                  <div style={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems: 'center' }}>
                    <div className="pdf-preview" style={{ overflow: 'auto', backgroundColor: '#f1f5f9' }}>
                      {previewUrl.startsWith('blob:') || previewUrl.startsWith('data:') ? (
                        <CanvasPDFViewer url={previewUrl} />
                      ) : (
                        <iframe
                          src={`https://docs.google.com/viewer?url=${encodeURIComponent(previewUrl)}&embedded=true`}
                          title="PDF Preview"
                          style={{ width: '100%', height: '100%', border: 'none' }}
                        />
                      )}
                    </div>
                    <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                      📄 PDF Document Preview.
                    </p>
                  </div>
                ) : isImage ? (
                  <div className="image-preview">
                    <img
                      src={previewUrl}
                      alt={`Page ${pageNum} preview`}
                      style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain' }}
                    />
                    <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                      🖼️ Image Preview.
                    </p>
                  </div>
                ) : (
                  <div className="unsupported-preview">
                    <p>Preview not available for this file type.</p>
                    <p>File: {fileInfo.name}</p>
                  </div>
                )}
              </div>

              <div className="preview-actions">
                <button
                  className="cancel-button"
                  onClick={cancelPreview}
                  disabled={isUploading || isNotifying}
                >
                  ❌ Close
                </button>
                {fileInfo.file && fileInfo.status === 'selected' && (
                  <button
                    className="confirm-button"
                    onClick={confirmUpload}
                    disabled={isUploading || isNotifying}
                  >
                    {isUploading ? 'Uploading...' : isNotifying ? 'Notifying...' : '✅ Confirm & Send for Review'}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Page Upload Popup Modal */}
      {showUploadPopup && (
        <div className="popup-modal-overlay">
          <div className="popup-modal">
            <div className="popup-header">
              <h3>Upload Pages - {selectedNewspaper} ({selectedEdition} Edition)</h3>
              <button 
                className="close-button" 
                onClick={() => {
                  setShowUploadPopup(false);
                  setPopupSelectedFiles({});
                  setPopupUploadStatus({});
                  setPopupUploadProgress({});
                  setPopupUploadError({});
                }}
              >
                ×
              </button>
            </div>

            <div className="popup-info">
              <p><strong>Selected Date:</strong> {formatDisplayDate(selectedDate)}</p>
              <p style={{ fontSize: '13px', color: '#666', margin: '5px 0 0 0' }}>
                💡 Select a file for each page and click "Upload" next to it. Accepted files: PDF, JPG, PNG.
              </p>
            </div>

            <div className="popup-content">
              <div className="popup-pages-list">
                {Array.from({ length: selectedNewspaper === 'Shikhar Sanket' ? 1 : 8 }, (_, i) => i + 1).map(pageNum => {
                  const fileInfo = uploadedFiles[selectedDate]?.[selectedNewspaper]?.[selectedEdition]?.[pageNum - 1];
                  const status = fileInfo?.status || 'not_uploaded';
                  
                  const isSelected = !!popupSelectedFiles[pageNum];
                  const uploadState = popupUploadStatus[pageNum]; // 'uploading' | 'success' | 'error'
                  const progress = popupUploadProgress[pageNum] || 0;
                  const errorMsg = popupUploadError[pageNum];
                  const selectedFile = popupSelectedFiles[pageNum];

                  return (
                    <div key={pageNum} className={`popup-page-row ${status}`}>
                      <div className="popup-page-meta">
                        <span className="popup-page-title">
                          Page {pageNum} {pageNum !== 1 && pageNum !== 8 && selectedNewspaper !== 'Shikhar Sanket' ? '(Common)' : '(Unique)'}
                        </span>
                        <span className={`status-badge ${status}`}>
                          {status === 'accepted' ? '✅ Accepted' :
                           status === 'review' ? '⏳ In Review' :
                           status === 'rejected' ? '❌ Rejected' : '⚪ Not Uploaded'}
                        </span>
                      </div>

                      <div className="popup-page-actions">
                        {status !== 'accepted' && status !== 'review' ? (
                          <div className="upload-controls-wrapper">
                            <div className="file-select-btn-container">
                              <label className="custom-file-upload-btn">
                                📂 {selectedFile ? 'Change File' : 'Choose File'}
                                <input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={(e) => handlePopupFileChange(e, pageNum)}
                                  disabled={uploadState === 'uploading'}
                                  style={{ display: 'none' }}
                                />
                              </label>
                              {selectedFile && (
                                <span className="selected-filename" title={selectedFile.name}>
                                  {selectedFile.name}
                                </span>
                              )}
                            </div>

                            {selectedFile && (
                              <button
                                type="button"
                                className="popup-upload-btn"
                                onClick={() => uploadSinglePageFromPopup(pageNum)}
                                disabled={uploadState === 'uploading'}
                              >
                                {uploadState === 'uploading' ? 'Uploading...' : 'Upload'}
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="upload-disabled-text">
                            {status === 'accepted' ? 'Approved by Admin' : 'Awaiting Admin Review'}
                          </span>
                        )}
                      </div>

                      {uploadState === 'uploading' && (
                        <div className="popup-row-progress-container">
                          <div className="popup-row-progress-bar" style={{ width: `${progress}%` }}></div>
                          <span className="popup-row-progress-text">{progress.toFixed(0)}%</span>
                        </div>
                      )}

                      {uploadState === 'success' && (
                        <div className="popup-row-message success">
                          ✓ Page {pageNum} uploaded successfully and sent for review!
                        </div>
                      )}

                      {uploadState === 'error' && (
                        <div className="popup-row-message error">
                          ✗ Error: {errorMsg}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="popup-actions">
              <button 
                className="cancel-button" 
                onClick={() => {
                  setShowUploadPopup(false);
                  setPopupSelectedFiles({});
                  setPopupUploadStatus({});
                  setPopupUploadProgress({});
                  setPopupUploadError({});
                }}
              >
                Close Panel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadPage;